import { useMemo, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Avatar } from './Avatar.tsx'
import { MessageItem } from './MessageItem.tsx'
import { Composer } from './Composer.tsx'
import { MessageMenu } from './MessageMenu.tsx'
import { ChatInfo } from './ChatInfo.tsx'
import { ChatSearch } from './ChatSearch.tsx'
import { Icon } from './Icon.tsx'
import { PinnedBar } from './PinnedBar.tsx'
import { SavedIcon } from './SavedIcon.tsx'
import { ReadBy } from './ReadBy.tsx'
import { useCall } from '../store/call.ts'
import { ForwardDialog } from './ForwardDialog.tsx'
import type { Message } from '../api/types.ts'
import { copyText } from '../lib/clipboard.ts'
import { say } from '../store/toast.ts'
import { useChats } from '../store/chats.ts'
import { sendFocus } from '../realtime/socket.ts'
import { useAuth } from '../store/auth.ts'
import { dateSeparator, lastSeenText, plural, sameDay } from '../lib/format.ts'

export function ChatView() {
  const { chatId } = useParams<{ chatId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const openChat = useChats((s) => s.openChat)
  const loadOlder = useChats((s) => s.loadOlder)
  const markRead = useChats((s) => s.markRead)
  const deleteMessage = useChats((s) => s.deleteMessage)
  const toggleReaction = useChats((s) => s.toggleReaction)
  const muteChat = useChats((s) => s.muteChat)
  const chatSummary = useChats((s) => s.chats.find((c) => c.id === chatId))
  const togglePin = useChats((s) => s.togglePin)
  const loadAround = useChats((s) => s.loadAround)
  const me = useAuth((s) => s.user)

  const detail = useChats((s) => (chatId ? s.detail[chatId] : undefined))
  const sent = useChats((s) => (chatId ? s.messages[chatId] : undefined))
  // Растёт при любом изменении очереди: без него лента не перерисуется,
  // когда сообщение наконец уйдёт
  const outboxVersion = useChats((s) => s.outboxVersion)
  const pendingFor = useChats((s) => s.pendingFor)
  const dropPending = useChats((s) => s.dropPending)

  /**
   * Неотправленное показывается в конце ленты обычными сообщениями,
   * только с часиками. Иначе человек не понимает, ушло оно или он
   * его вообще не написал.
   */
  const messages = useMemo(() => {
    if (!sent || !chatId) return sent
    const waiting = pendingFor(chatId, me ? { id: me.id, displayName: me.displayName } : null)
    return waiting.length > 0 ? [...sent, ...waiting] : sent
  }, [sent, chatId, outboxVersion, me])
  const hasMore = useChats((s) => (chatId ? s.hasMore[chatId] : false))
  const typing = useChats((s) => (chatId ? s.typing[chatId] : undefined))

  const scrollRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ message: Message; at: { x: number; y: number } } | null>(null)
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [editing, setEditing] = useState<Message | null>(null)
  const [atBottom, setAtBottom] = useState(true)
  const [highlighted, setHighlighted] = useState<string | null>(null)
  /** Где человек остановился в прошлый раз — отсюда рисуется черта «новые» */
  const [unreadMark, setUnreadMark] = useState<string | null>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const [readBy, setReadBy] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const startCall = useCall((s) => s.start)
  /** Выделение нескольких: пусто — обычный режим, иначе режим выбора */
  const [selected, setSelected] = useState<string[]>([])

  /**
   * Скопировать выбранное.
   *
   * Порядок берём из ленты, а не из очерёдности нажатий: человек мог
   * отметить сначала ответ, потом вопрос — в буфере они всё равно
   * должны идти так, как шли в переписке. Имя подписываем только там,
   * где собеседников больше одного: в разговоре вдвоём это шум.
   */
  const copySelected = async () => {
    const chosen = new Set(selected)
    const rows = (messages ?? []).filter((m) => chosen.has(m.id))
    if (rows.length === 0) return

    const named = (detail?.members.length ?? 0) > 2
    const text = rows
      .map((m) => {
        const body = m.body ?? (m.files.length > 0 ? '[вложение]' : '')
        const who = m.author?.displayName
        return named && who ? `${who}: ${body}` : body
      })
      .filter((line) => line.length > 0)
      .join('\n')

    // Выбор снимаем только если вправду скопировалось: иначе человек
    // решит, что всё получилось, и вставит куда-то старое
    if (await copyText(text)) {
      say(rows.length === 1 ? 'Скопировано' : `Скопировано: ${rows.length}`)
      setSelected([])
    } else {
      say('Браузер не дал доступ к буферу', 'bad')
    }
  }
  const [forwarding, setForwarding] = useState<string[] | null>(null)
  /** Повторная попытка открыть переписку после неудачи */
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    if (!chatId) return
    setError(null)
    setReplyTo(null)
    setEditing(null)
    setMenu(null)
    setUnreadMark(null)
    setHighlighted(null)
    setSelected([])
    setInfoOpen(false)
    openChat(chatId).catch((e) =>
      setError(e instanceof Error ? e.message : 'Не удалось открыть переписку'),
    )
  }, [chatId, openChat, retry])

  // Отметку «дочитано до» берём один раз при открытии: дальше она
  // сдвинется на сервере, и черта «новые сообщения» исчезнет прямо
  // под курсором — а она нужна, пока человек не ушёл из переписки
  useEffect(() => {
    if (!detail || !me || unreadMark !== null) return
    const mine = detail.members.find((m) => m.id === me.id)
    setUnreadMark(mine?.lastReadAt ?? '')
  }, [detail, me, unreadMark])

  const count = messages?.length ?? 0
  const lastId = messages?.at(-1)?.id

  /**
   * Прокрутка вниз при новом сообщении.
   *
   * Тянем вниз в двух случаях: человек и так стоял внизу — или он сам
   * только что написал. Своё сообщение всегда показываем: иначе оно
   * уходит куда-то за край, и непонятно, отправилось ли.
   *
   * А вот читающего старую переписку дёргать нельзя. Раньше здесь была
   * поблажка «если сообщений меньше полусотни» — и в молодой переписке
   * любое чужое сообщение сбрасывало чтение на низ.
   */
  /**
   * Перед каким сообщением встаёт черта «Новые сообщения».
   *
   * Считаем один раз на всю ленту, а не по ходу отрисовки: условие
   * «первое чужое после моего» в разговоре, где реплики чередуются,
   * срабатывает десяток раз, и черта расчерчивает всю переписку.
   *
   * Пустая отметка — переписку не открывали ни разу: тогда непрочитано
   * всё, и черта идёт перед первым чужим сообщением.
   */
  const firstUnreadId = useMemo(() => {
    if (unreadMark === null || !messages) return null

    for (const message of messages) {
      if (message.mine) continue
      if (unreadMark === '') return message.id
      if (new Date(message.createdAt) > new Date(unreadMark)) return message.id
    }
    return null
  }, [messages, unreadMark])

  const lastIsMine = messages?.at(-1)?.mine === true

  /**
   * Какую переписку мы уже поставили в конец.
   *
   * Отдельным действием «сбросить признак» это не решается: обычное
   * действие выполняется после расстановки на экране, и признак
   * успевает вернуться до следующего сообщения. Поэтому храним
   * не «первый ли раз», а «какую именно».
   */
  const settledChat = useRef<string | null>(null)
  const unreadRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const box = scrollRef.current
    if (!box || count === 0 || !chatId) return

    /**
     * Открыли переписку — встаём туда, где остановились.
     *
     * Если непрочитанное есть, это черта «Новые сообщения», а не конец
     * ленты: за вечер в семейной группе набегает четыре десятка
     * сообщений, и человеку нужно начало непрочитанного, а не хвост.
     * Черту ставим повыше края, чтобы было видно, на что отвечали.
     */
    if (settledChat.current !== chatId) {
      /**
       * Отметка «дочитано до» появляется на такт позже первой
       * расстановки. Пока её нет, встаём в конец, но решение не
       * закрываем — иначе черту «Новые сообщения» не увидит никто.
       */
      if (unreadMark === null) {
        bottomRef.current?.scrollIntoView({ block: 'end' })
        return
      }

      settledChat.current = chatId
      if (unreadRef.current) {
        unreadRef.current.scrollIntoView({ block: 'start' })
        box.scrollTop = Math.max(0, box.scrollTop - 48)
      } else {
        bottomRef.current?.scrollIntoView({ block: 'end' })
      }
      return
    }

    const distanceFromBottom = box.scrollHeight - box.scrollTop - box.clientHeight
    if (distanceFromBottom < 200 || lastIsMine) {
      bottomRef.current?.scrollIntoView({ block: 'end' })
    }
  }, [lastId, count, lastIsMine, chatId, unreadMark])

  useEffect(() => {
    if (!chatId || count === 0) return
    markRead(chatId)
  }, [chatId, count, markRead])

  /**
   * Клавиатура открылась — остаёмся там же, где были.
   *
   * Видимая часть экрана уменьшается вдвое, лента вместе с ней, и
   * последнее сообщение уезжает под клавиатуру: человек нажал «ответить»
   * и потерял из виду то, на что отвечает. Если он стоял внизу — там
   * и оставляем; если читал старое — не трогаем вовсе.
   */
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const keepPlace = () => {
      if (!atBottom) return
      // Дожидаемся, пока раскладка уляжется под новую высоту
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: 'end' }))
    }

    viewport.addEventListener('resize', keepPlace)
    return () => viewport.removeEventListener('resize', keepPlace)
  }, [atBottom])

  /**
   * Переход к сообщению по адресу вида /chat/…?at=…
   * Так работает нажатие на найденное в поиске: переписка открывается
   * сразу на нужном месте, а не в конце.
   *
   * Метку из адреса убираем после перехода — иначе возврат «назад»
   * в браузере снова прыгал бы на то же сообщение.
   */
  const jumpTarget = searchParams.get('at')
  const jumpHandled = useRef<string | null>(null)

  useEffect(() => {
    if (!jumpTarget || !chatId || count === 0) return
    if (jumpHandled.current === jumpTarget) return

    let cancelled = false

    const scroll = () => {
      const element = document.getElementById('msg-' + jumpTarget)
      if (!element) return false
      element.scrollIntoView({ block: 'center' })
      setHighlighted(jumpTarget)
      window.setTimeout(() => !cancelled && setHighlighted(null), 1600)
      return true
    }

    const go = async () => {
      jumpHandled.current = jumpTarget
      if (!scroll()) {
        try {
          await loadAround(jumpTarget)
        } catch {
          // Не нашлось — скажем, и уберём метку из адреса, чтобы
          // «назад» не приводило сюда же снова
          if (!cancelled) {
            say('Этого сообщения больше нет', 'bad')
            setSearchParams({}, { replace: true })
          }
          return
        }
        if (cancelled) return
        requestAnimationFrame(scroll)
      }
      setSearchParams({}, { replace: true })
    }

    void go()
    return () => {
      cancelled = true
    }
  }, [jumpTarget, chatId, count, loadAround, setSearchParams])

  // Пока переписка открыта, уведомления по ней не приходят
  useEffect(() => {
    if (!chatId) return
    sendFocus(chatId)
    return () => sendFocus(null)
  }, [chatId])

  const onScroll = async () => {
    const box = scrollRef.current
    if (!box) return

    const fromBottom = box.scrollHeight - box.scrollTop - box.clientHeight
    setAtBottom(fromBottom < 120)

    if (!chatId || !hasMore || loadingOlder) return
    if (box.scrollTop > 120) return

    setLoadingOlder(true)
    const previousHeight = box.scrollHeight
    try {
      await loadOlder(chatId)
      // Сохраняем положение: после дописывания сверху лента иначе прыгает
      requestAnimationFrame(() => {
        box.scrollTop = box.scrollHeight - previousHeight
      })
    } finally {
      setLoadingOlder(false)
    }
  }

  /**
   * Перейти к сообщению: если оно уже загружено — просто прокручиваем,
   * если нет — догружаем кусок истории вокруг него. Подсветка нужна,
   * чтобы глаз нашёл нужную строку среди похожих.
   */
  const jumpTo = async (messageId: string) => {
    const scrollToIt = () => {
      const element = document.getElementById('msg-' + messageId)
      if (!element) return false
      element.scrollIntoView({ block: 'center', behavior: 'smooth' })
      setHighlighted(messageId)
      window.setTimeout(() => setHighlighted(null), 1600)
      return true
    }

    if (scrollToIt()) return

    try {
      await loadAround(messageId)
      requestAnimationFrame(scrollToIt)
    } catch {
      /**
       * Не нашлось — говорим строкой внизу и оставляем ленту на месте.
       *
       * Раньше неудачный переход заменял всю переписку надписью, и
       * выхода из неё не было: ни ленты, ни кнопки «назад». А повод
       * частый — сообщение, к которому шли, могли удалить.
       */
      say('Этого сообщения больше нет', 'bad')
    }
  }

  if (!chatId) return null

  /**
   * Переписка не открылась совсем.
   *
   * Это единственный случай, когда вместо ленты — сообщение: показывать
   * нечего. Но выход должен быть всегда: на телефоне этот экран занимает
   * всё, и без кнопки «назад» из него было не уйти.
   */
  if (error) {
    return (
      <div className="chat-view">
        <header className="chat-head">
          <Link to="/" className="icon-btn back-btn" aria-label="Назад">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z" />
            </svg>
          </Link>
        </header>
        <div className="center-screen">
          <p className="empty-hint">{error}</p>
          <button className="btn-quiet" onClick={() => setRetry((n) => n + 1)}>
            Попробовать снова
          </button>
        </div>
      </div>
    )
  }

  if (!detail || !messages) {
    return (
      <div className="chat-view">
        <div className="center-screen">
          <div className="spinner" />
        </div>
      </div>
    )
  }

  const isGroup = detail.kind !== 'DIRECT'

  /**
   * Прочитано, если каждый собеседник дочитал дальше этого сообщения.
   * Сравниваем по времени, а не по каждому сообщению отдельно: одной
   * отметки на человека достаточно, а запросов она экономит много.
   */
  const others = detail.members.filter((m) => m.id !== me?.id)
  const readUpTo = others.length
    ? Math.min(...others.map((m) => (m.lastReadAt ? new Date(m.lastReadAt).getTime() : 0)))
    : 0
  const companion = detail.members.find((m) => m.id !== undefined && detail.kind === 'DIRECT')
  const activeTyping = (typing ?? []).filter((t) => t.until > Date.now())

  const subtitle = activeTyping.length
    ? activeTyping.map((t) => t.displayName).join(', ') + ' печатает…'
    : isGroup
      ? `${detail.members.length} ${plural(detail.members.length, 'участник', 'участника', 'участников')}`
      : lastSeenText(companion?.lastSeenAt ?? null, companion?.online ?? false)

  return (
    <div className="chat-view">
      <header className="chat-head">
        <Link to="/" className="icon-btn back-btn" aria-label="Назад">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z" />
          </svg>
        </Link>

        <button className="chat-head-open" onClick={() => setInfoOpen(true)} title="Сведения">
          {detail.kind === 'SAVED' ? (
            <SavedIcon size={40} />
          ) : (
            <Avatar id={detail.id} name={detail.title} url={detail.avatarUrl} size={40} />
          )}
          <div className="chat-head-text">
            <div className="chat-head-title">{detail.title}</div>
            <div className={'chat-head-sub' + (activeTyping.length ? ' is-typing' : '')}>
              {subtitle}
            </div>
          </div>
        </button>

        {/* Звонить можно только там, где есть кому: в переписке
            с самим собой кнопка бессмысленна */}
        {detail.members.length > 1 && (
          <>
            <button
              className="icon-btn head-action"
              onClick={() => void startCall(chatId, 'AUDIO')}
              title="Позвонить"
              aria-label="Позвонить"
            >
              <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor"
                   strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6.5 4h3l1.5 4-2 1.5a12 12 0 0 0 5.5 5.5l1.5-2 4 1.5v3a1.5 1.5 0 0 1-1.7 1.5A16.5 16.5 0 0 1 5 6.2 1.5 1.5 0 0 1 6.5 4z" />
              </svg>
            </button>

            <button
              className="icon-btn head-action"
              onClick={() => void startCall(chatId, 'VIDEO')}
              title="Видеозвонок"
              aria-label="Видеозвонок"
            >
              <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor"
                   strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="6" width="12" height="12" rx="2" />
                <path d="m15 10.5 5-3v9l-5-3z" />
              </svg>
            </button>
          </>
        )}

        <button
          className="icon-btn head-action"
          onClick={() => setSearchOpen((open) => !open)}
          title="Поиск в переписке"
          aria-label="Поиск в переписке"
        >
          <Icon name="search" size={20} />
        </button>

        <button
          className={'icon-btn head-action' + (chatSummary?.muted ? ' is-muted' : '')}
          onClick={() => void muteChat(chatId, chatSummary?.muted ? null : 8)}
          title={chatSummary?.muted ? 'Включить уведомления' : 'Заглушить на 8 часов'}
          aria-label={chatSummary?.muted ? 'Включить уведомления' : 'Заглушить'}
        >
          {chatSummary?.muted ? '🔕' : '🔔'}
        </button>
      </header>

      {searchOpen && (
        <ChatSearch
          chatId={chatId}
          members={detail.members}
          onJump={(id) => void jumpTo(id)}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {selected.length > 0 && (
        <div className="select-bar">
          <button className="icon-btn" onClick={() => setSelected([])} aria-label="Отменить выбор">
            ✕
          </button>
          <span className="select-count">
            <span className="select-word">Выбрано: </span>
            {selected.length}
          </span>
          <button className="select-action" onClick={() => setForwarding(selected)}>
            Переслать
          </button>
          <button className="select-action" onClick={() => void copySelected()}>
            Копировать
          </button>
          <button
            className="select-action is-danger"
            onClick={() => {
              if (!window.confirm(`Удалить выбранное (${selected.length})?`)) return
              for (const id of selected) void deleteMessage(chatId, id)
              setSelected([])
            }}
          >
            Удалить
          </button>
        </div>
      )}

      {/* Закреплённых бывает несколько: шапка перебирает их по нажатию
          и показывает, на котором ты сейчас */}
      <PinnedBar chatId={chatId} onJump={(id) => void jumpTo(id)} />

      <div className="messages scroll" ref={scrollRef} onScroll={() => void onScroll()}>
        {loadingOlder && (
          <div className="load-older">
            <div className="spinner" />
          </div>
        )}

        {!hasMore && messages.length > 0 && (
          <div className="chat-start">Начало переписки</div>
        )}

        {messages.length === 0 && (
          <div className="center-screen">
            <p className="empty-hint">Здесь пока пусто. Напиши первым.</p>
          </div>
        )}

        {messages.map((message, index) => {
          const previous = messages[index - 1]
          const newDay = !previous || !sameDay(previous.createdAt, message.createdAt)

          // Серия сообщений одного человека подряд: имя и аватар только у первого
          const showAuthor =
            newDay ||
            !previous ||
            previous.author?.id !== message.author?.id ||
            previous.isSystem !== message.isSystem

          /**
           * Черта ставится перед первым сообщением, пришедшим после
           * того, как человек в прошлый раз дочитал. Своё под чертой
           * не считается.
           *
           * Пустая отметка — переписку не открывали ни разу: тогда
           * непрочитано всё, и черта идёт перед первым чужим. Раньше
           * такой случай пропускали совсем, и в только что заведённой
           * группе с тремя десятками сообщений черты не было вовсе.
           */
          const isFirstUnread = message.id === firstUnreadId

          return (
            <div key={message.id} id={'msg-' + message.id}>
              {newDay && (
                <div className="date-separator">
                  <span>{dateSeparator(message.createdAt)}</span>
                </div>
              )}

              {isFirstUnread && (
                <div className="unread-line" ref={unreadRef}>
                  <span>Новые сообщения</span>
                </div>
              )}
              <div className={highlighted === message.id ? 'msg-highlight' : undefined}>
                <MessageItem
                  message={message}
                  showAuthor={showAuthor}
                  isGroup={isGroup}
                  /* У неотправленного меню не открываем: править и
                     пересылать нечего, пока оно не ушло. Вместо меню —
                     предложение убрать, если передумал */
                  onMenu={(m, at) =>
                    m.pending
                      ? m.failed && window.confirm('Убрать неотправленное сообщение?')
                        ? dropPending(m.id)
                        : undefined
                      : setMenu({ message: m, at })
                  }
                  onReaction={(id, emoji) => void toggleReaction(id, emoji)}
                  readByOthers={new Date(message.createdAt).getTime() <= readUpTo}
                  onJumpTo={(id) => void jumpTo(id)}
                  selecting={selected.length > 0}
                  isSelected={selected.includes(message.id)}
                  onToggleSelect={() =>
                    setSelected((current) =>
                      current.includes(message.id)
                        ? current.filter((id) => id !== message.id)
                        : [...current, message.id],
                    )
                  }
                  onReply={(m) => {
                    setEditing(null)
                    setReplyTo(m)
                  }}
                />
              </div>
            </div>
          )
        })}

        <div ref={bottomRef} />
      </div>

      {!atBottom && (
        <button
          className="to-bottom"
          onClick={() => bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })}
          aria-label="Вниз"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M12 16.5 5.5 10 7 8.5l5 5 5-5L18.5 10z" />
          </svg>
          {(chatSummary?.unread ?? 0) > 0 && (
            <span className="to-bottom-count">{chatSummary?.unread}</span>
          )}
        </button>
      )}

      <Composer
        chatId={chatId}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        editing={editing}
        onCancelEdit={() => setEditing(null)}
      />

      {infoOpen && (
        <ChatInfo chat={detail} onClose={() => setInfoOpen(false)} onJumpTo={(id) => void jumpTo(id)} />
      )}

      {forwarding && (
        <ForwardDialog
          messageIds={forwarding}
          fromChatId={chatId}
          onClose={() => setForwarding(null)}
          onDone={() => setSelected([])}
        />
      )}

      {menu && (
        <MessageMenu
          message={menu.message}
          at={menu.at}
          onClose={() => setMenu(null)}
          onReply={() => {
            setEditing(null)
            setReplyTo(menu.message)
          }}
          onEdit={() => {
            setReplyTo(null)
            setEditing(menu.message)
          }}
          onDelete={() => void deleteMessage(chatId, menu.message.id)}
          /**
           * Закрепление живёт на самом сообщении, а не на переписке:
           * закреплённых может быть несколько, и каждое снимается
           * по отдельности.
           */
          onPin={() => void togglePin(menu.message.id, !menu.message.pinned)}
          isPinned={Boolean(menu.message.pinned)}
          onForward={() => setForwarding([menu.message.id])}
          onSelect={() => setSelected([menu.message.id])}
          onReaction={(emoji) => void toggleReaction(menu.message.id, emoji)}
          onReadBy={
            detail.members.length > 2 ? () => setReadBy(menu.message.id) : undefined
          }
        />
      )}

      {readBy && <ReadBy messageId={readBy} onClose={() => setReadBy(null)} />}
    </div>
  )
}
